// src/oura/components/newscan/SearchesView.tsx
//
// The Searches view of the Review step, brand mode only (v4.5, SPEC-v45 §7.3),
// in place of Words: the Google searches the 50 were written from.
//   - one line: how many were pulled, for which markets, on which day, and how
//     many of the 50 come from them;
//   - the questions no search supports, first, each one a button that opens it
//     in Questions with its editor open;
//   - All / Used / Not used chips;
//   - one fold per market in the business's order, then "Any market": each
//     search as Google wrote it and "Used in 7, 12" (each number jumps to that
//     question) or "Not used";
//   - a last fold, "Words in the questions": the few words brand rows use, so a
//     word decision's "Type one" still lands somewhere;
//   - "Pull the searches again" at the foot.
//
// A payload without the list (`demand.items` absent) shows the line alone and
// says the list is not available.

import { RefreshCwIcon, SearchXIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ScanDraft, ScanDraftDemandSuggestion, ScanDraftEffect, ScanDraftQuestion } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import {
    FOCUS_RING,
    FoldScope,
    GroupFold,
    ListRow,
    PillButton,
    StatusKey,
    SURFACE,
    Surface,
    TYPE,
    useFoldOpener,
    useMediaQuery,
    REDUCED_MOTION,
} from "@oura/ui";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { useWritesPaused } from "@context/maintenance";
import { plainRefusal, plural } from "@oura/newscan/copy";
import { capital, marketName, marketOf, marketOrder, pulledMarkets, shortDate } from "./brandReview";
import { Muted } from "./Muted";
import { WordRow } from "./WordRow";
import { focusWordField } from "./wordFocus";

const SCOPE = "newscan-searches";
const WORDS_ID = "searches-words";
const FINE_ID = "newscan-searches-again-fine";

type Filter = "all" | "used" | "unused";

const COPY = {
    intro: (n: number, markets: string, date: string | null) =>
        `${plural(n, "Google search", "Google searches")} pulled${markets ? ` for ${markets}` : ""}${date ? ` on ${date}` : ""}.`,
    fromThem: (k: number, total: number) => `${k} of ${total} questions come from them.`,
    noList: "The list is not available for this draft.",
    untracedTitle: (n: number) => `${plural(n, "question")} ${n === 1 ? "has" : "have"} no Google search behind ${n === 1 ? "it" : "them"}`,
    filterLabel: "Show searches",
    used: "Used",
    unused: "Not used",
    groupNote: (questions: number, searches: number) => `${plural(questions, "question")} · ${plural(searches, "search", "searches")}`,
    usedIn: "Used in",
    question: (n: number) => `Question ${n}`,
    none: "No searches match.",
    words: "Words in the questions",
    again: "Pull the searches again",
    againFine: "Takes about a minute. Questions you changed keep their wording.",
    changed: (n: number) => (n === 0 ? "No question changed." : `Changed in ${plural(n, "question")}.`),
    skipped: (n: number) => (n === 0 ? "" : ` ${plural(n, "locked question")} kept ${n === 1 ? "its" : "their"} wording.`),
} as const;

const effectLine = (e: ScanDraftEffect) => `${COPY.changed(e.changed.length)}${COPY.skipped(e.skipped.length)}`;

/** A text button to a question number, with a 44px target. */
const NUMBER_LINK = cn(
    "relative rounded font-medium tabular-nums text-blue-600 hover:underline after:absolute after:-inset-x-1 after:-inset-y-3 after:content-['']",
    FOCUS_RING,
);

export interface SearchesViewProps {
    /** Opens a question in Questions with its editor open (an untraced row). */
    onOpenQuestion(n: number): void;
    /** Jumps to a question in Questions ("Used in 7"). */
    onShowQuestion(n: number): void;
    /** A word to bring into view and focus (a word decision's "Type one", a "Built from" link). */
    focusVariable?: string | null;
    onFocusDone?: () => void;
}

export function SearchesView(props: SearchesViewProps) {
    return (
        <FoldScope value={SCOPE}>
            <SearchesBody {...props} />
        </FoldScope>
    );
}

export default SearchesView;

function SearchesBody({ onOpenQuestion, onShowQuestion, focusVariable, onFocusDone }: SearchesViewProps) {
    const { ctl, goTo, canReach } = useScanFlow();
    const draft = ctl.draft;
    const openGroup = useFoldOpener(SCOPE);
    const reduced = useMediaQuery(REDUCED_MOTION);
    const root = useRef<HTMLDivElement>(null);
    const [filter, setFilter] = useState<Filter>("all");
    const writesPaused = useWritesPaused();

    /* ---- the words fold: what the last save did, under its word ---- */
    const [result, setResult] = useState<{ variable: string; text: string } | null>(null);
    const pendingWord = useRef<string | null>(null);
    const outcome = ctl.outcome;
    useEffect(() => {
        if (!outcome) return;
        const variable = pendingWord.current;
        pendingWord.current = null;
        setResult(variable ? { variable, text: effectLine(outcome) } : null);
    }, [outcome]);
    const patchWord = ctl.patchWord;
    const labelsRef = useRef(new Map<string, string>());
    labelsRef.current = new Map((draft?.words ?? []).map((w) => [w.variable, w.label]));
    const saveWord = useCallback(
        async (variable: string, value: string) => {
            pendingWord.current = variable;
            const failure = await patchWord(variable, value);
            if (failure) {
                pendingWord.current = null;
                setResult((was) => (was?.variable === variable ? null : was));
                // The lint names words in braces: say them by their labels, and what to do.
                if (failure.kind !== "stale") return { ...failure, message: plainRefusal(failure.message, (v) => labelsRef.current.get(v)) };
            }
            return failure;
        },
        [patchWord],
    );

    const byN = useMemo(() => {
        const map = new Map<number, ScanDraftQuestion>();
        draft?.questions.forEach((q) => map.set(q.n, q));
        return map;
    }, [draft]);
    // The words brand rows use: the rest write no question here.
    const words = useMemo(() => (draft?.words ?? []).filter((w) => w.usedBy.length > 0), [draft]);

    /* ---- bring one word into view ---- */
    const [openedFrom, setOpenedFrom] = useState<string | null>(null);
    useEffect(() => {
        if (!focusVariable) return;
        const word = words.find((w) => w.variable === focusVariable);
        if (!word) {
            onFocusDone?.();
            return;
        }
        openGroup(WORDS_ID, { persist: false });
        setOpenedFrom(word.variable);
        // The fold renders its rows a frame or two after it opens: focus the field the moment it exists.
        return focusWordField(root.current, word.variable, reduced, () => onFocusDone?.());
        // Once per request: the word list refreshing must not pull focus back.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusVariable]);

    if (!draft) return null;
    const demand = draft.demand;
    const readOnly = draft.status !== "ready";
    const busy = ctl.busy !== null;
    const total = draft.questions.length;
    const untraced = (demand?.untraced ?? []).filter((n) => byN.has(n)).sort((a, b) => a - b);
    const items = demand?.items ?? null;

    return (
        <div ref={root} className="space-y-4">
            <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                {demand ? (
                    <>
                        <span className="tabular-nums">{COPY.intro(demand.suggestions, pulledMarkets(draft), shortDate(demand.pulledAt))}</span>{" "}
                        <span className="tabular-nums">{COPY.fromThem(Math.max(0, total - untraced.length), total)}</span>
                        {!items && <> {COPY.noList}</>}
                    </>
                ) : (
                    COPY.noList
                )}
            </p>

            {untraced.length > 0 && (
                <section aria-labelledby="newscan-untraced" className={cn(SURFACE.inner, "space-y-1.5")}>
                    <h3 id="newscan-untraced" className={cn(TYPE.tc, "m-0 flex items-center gap-1.5 font-medium text-gray-900")}>
                        <SearchXIcon aria-hidden size={14} className="shrink-0 text-amber-600" />
                        {COPY.untracedTitle(untraced.length)}
                    </h3>
                    <ul role="list" className="list-plain m-0 space-y-0.5 p-0">
                        {untraced.map((n) => (
                            <li key={n} className="flex min-w-0 items-start gap-2">
                                <span className={cn(TYPE.tc, "w-6 shrink-0 pt-1 text-right tabular-nums text-gray-400")}>{n}</span>
                                <button
                                    type="button"
                                    onClick={() => onOpenQuestion(n)}
                                    className={cn(
                                        TYPE.tb,
                                        "relative min-w-0 flex-1 rounded py-0.5 text-left text-blue-700 hover:underline",
                                        "after:absolute after:-inset-y-2 after:inset-x-0 after:content-['']",
                                        FOCUS_RING,
                                    )}
                                >
                                    <span className="sr-only">{COPY.question(n)}: </span>
                                    <span className="break-words">{byN.get(n)?.text}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* An empty list still carries the words fold: a "Type one" decision lands there (Jack, #4287). */}
            {items !== null && (
                <SearchList
                    draft={draft}
                    items={items}
                    filter={filter}
                    onFilter={setFilter}
                    onShowQuestion={onShowQuestion}
                    words={
                        words.length > 0 ? (
                            <GroupFold
                                id={WORDS_ID}
                                tone="violet"
                                lead="dot"
                                title={COPY.words}
                                count={words.length}
                                defaultFolded={!openedFrom}
                                remember={false}
                                wrap
                            >
                                <div>
                                    {words.map((w) => (
                                        <WordRow
                                            key={w.variable}
                                            word={w}
                                            questions={w.usedBy.map((n) => byN.get(n)).filter((q): q is ScanDraftQuestion => Boolean(q))}
                                            onSave={(value) => saveWord(w.variable, value)}
                                            result={result?.variable === w.variable ? result.text : null}
                                            readOnly={readOnly || writesPaused}
                                            onBusinessStep={canReach("business") ? () => goTo("business") : undefined}
                                            defaultOpen={openedFrom === w.variable}
                                            draftVersion={draft.version}
                                        />
                                    ))}
                                </div>
                            </GroupFold>
                        ) : null
                    }
                />
            )}

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
                <PillButton
                    variant="text"
                    icon={RefreshCwIcon}
                    writes busy={ctl.busy === "Drafting"}
                    disabled={readOnly || busy}
                    onClick={() => void ctl.rebuild({ refreshDemand: true })}
                    aria-describedby={FINE_ID}
                >
                    {COPY.again}
                </PillButton>
                <Muted id={FINE_ID} tone="ink4" size="tm">
                    {COPY.againFine}
                </Muted>
            </div>
        </div>
    );
}

/** The filter chips and one fold per market, then the words fold. */
function SearchList({
    draft,
    items,
    filter,
    onFilter,
    onShowQuestion,
    words,
}: {
    draft: ScanDraft;
    items: ScanDraftDemandSuggestion[];
    filter: Filter;
    onFilter(f: Filter): void;
    onShowQuestion(n: number): void;
    words: ReactNode;
}) {
    const used = items.filter((s) => s.usedBy.length > 0).length;
    const shown = items.filter((s) => (filter === "used" ? s.usedBy.length > 0 : filter === "unused" ? s.usedBy.length === 0 : true));

    // Markets in the business's order, then "Any market"; a code the business does not list keeps its place after them.
    const codes = useMemo(() => {
        const order = marketOrder(draft);
        const seen = new Set(items.map((s) => (s.market || "global").toLowerCase()));
        return [...order.filter((c) => seen.has(c)), ...Array.from(seen).filter((c) => c !== "global" && !order.includes(c)), ...(seen.has("global") ? ["global"] : [])];
    }, [draft, items]);
    const questionsIn = (code: string) => draft.questions.filter((q) => marketOf(q) === code).length;

    return (
        <div className="space-y-3">
            <StatusKey
                ariaLabel={COPY.filterLabel}
                value={filter}
                onChange={(k) => onFilter(k === "used" || k === "unused" ? k : "all")}
                total={items.length}
                groups={[
                    { key: "used", label: COPY.used, tone: "green", count: used },
                    { key: "unused", label: COPY.unused, tone: "gray", count: items.length - used },
                ]}
            />
            <Surface flush className="px-5 py-1">
                <div>
                    {codes.map((code, i) => {
                        const rows = shown
                            .filter((s) => (s.market || "global").toLowerCase() === code)
                            .sort((a, b) => b.recurs - a.recurs || a.text.localeCompare(b.text));
                        const name = marketName(code, draft.business);
                        const all = items.filter((s) => (s.market || "global").toLowerCase() === code).length;
                        return (
                            <GroupFold
                                key={code}
                                id={`market-${code}`}
                                tone="teal"
                                lead="dot"
                                title={code === "global" ? capital(name.full) : name.full}
                                count={rows.length}
                                defaultFolded={i > 0}
                                remember={false}
                                wrap
                                note={<span className={cn(TYPE.tc, "tabular-nums text-gray-500")}>{COPY.groupNote(questionsIn(code), all)}</span>}
                            >
                                {rows.length === 0 ? (
                                    <Muted as="p" className="py-3">
                                        {COPY.none}
                                    </Muted>
                                ) : (
                                    <div>
                                        {rows.map((s) => (
                                            <ListRow
                                                key={`${s.market}:${s.text}`}
                                                lead="dot"
                                                // An empty lead: the search lines up with the group title, as the 50 do.
                                                icon={<span aria-hidden />}
                                                title={s.text}
                                                stackMeta
                                                meta={
                                                    s.usedBy.length > 0 ? (
                                                        <UsedIn numbers={s.usedBy} onShowQuestion={onShowQuestion} />
                                                    ) : (
                                                        <span className="text-gray-400">{COPY.unused}</span>
                                                    )
                                                }
                                            />
                                        ))}
                                    </div>
                                )}
                            </GroupFold>
                        );
                    })}
                    {words}
                </div>
            </Surface>
        </div>
    );
}

/** "Used in 7, 12", each number a button to that question. */
function UsedIn({ numbers, onShowQuestion }: { numbers: number[]; onShowQuestion(n: number): void }) {
    const sorted = Array.from(new Set(numbers)).sort((a, b) => a - b);
    return (
        <span className="text-gray-500">
            {COPY.usedIn}{" "}
            {sorted.map((n, i) => (
                <span key={n}>
                    <button type="button" aria-label={COPY.question(n)} onClick={() => onShowQuestion(n)} className={NUMBER_LINK}>
                        {n}
                    </button>
                    {i < sorted.length - 1 ? ", " : ""}
                </span>
            ))}
        </span>
    );
}
